import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import Header from '../Header/Header';
import AdminHeader from '../Header/AdminHeader';
import UserGreeting from '../UserGreeting/UserGreeting';
import { useAuth } from 'react-oidc-context';
import useUserRole from '../../hooks/useUserRole';
import AuthDebug from '../AuthDebug/AuthDebug';
import './AuthenticatedLayout.scss';

// Get environment to conditionally show debug component
const isDevelopment = process.env.NODE_ENV === 'development';

interface AuthenticatedLayoutProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = ({
  children,
  requireAdmin = false
}) => {
  const auth = useAuth();
  const location = useLocation();
  const { isAdmin, isAuthenticated, isLoading } = useUserRole();

  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024
  });

  // Handle viewport changes
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      setViewport({
        width,
        height,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isLoading || auth.isLoading) {
    return (
      <div className="authenticated-layout__loading" role="alert" aria-live="assertive">
        <div className="authenticated-layout__loading-content">
          <div className="authenticated-layout__loading-text">Loading...</div>
          <div className="authenticated-layout__loading-subtitle">
            Checking permissions...
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (requireAdmin && !isAdmin) {
    return (
      <div className="authenticated-layout__unauthorized" role="alert">
        <div className="authenticated-layout__unauthorized-content">
          <h2>Access Denied</h2>
          <p>You need admin privileges to access this page.</p>
          <p>Please contact your administrator if you believe this is an error.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`authenticated-layout ${viewport.isMobile ? 'is-mobile' : ''} ${viewport.isTablet ? 'is-tablet' : ''}`}>
      {isAdmin ? <AdminHeader /> : <Header />}
      <main className="authenticated-layout__content">
        <div className="authenticated-layout__greeting">
          <UserGreeting />
        </div>
        {isDevelopment && <AuthDebug />}
        <div className="authenticated-layout__main-content">
          {children}
        </div>
      </main>
    </div>
  );
};

AuthenticatedLayout.propTypes = {
  children: PropTypes.node.isRequired,
  requireAdmin: PropTypes.bool
};

export default AuthenticatedLayout;